import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FontFamily, FontWeight, fontWeights } from "./element-schema";

export type Enableable<T> = { enabled: boolean } & T;

const hexCodeValidator = z.string();
export type HexCode = `${string}`;

export const fontWeightValues: Record<FontWeight, number> = {
  regular: 400,
  semibold: 600,
  bold: 700,
};

export function styleFromFontWeight(fontWeight: FontWeight) {
  switch (fontWeight) {
    case "regular":
      return "400";
    case "semibold":
      return "600";
    case "bold":
      return "700";
    default:
      assertNever(fontWeight);
  }
}

const fontStyleDefinitionValidator_ = z.object({
  fontSizePx: z.number(),
  fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }),
});
const fontStyleDefinitionValidator: z.ZodType<FontStyleDefinition> =
  fontStyleDefinitionValidator_;
export interface FontStyleDefinition extends z.infer<
  typeof fontStyleDefinitionValidator_
> {}

const borderStrokeStyleDefinitionValidator_ = z.object({
  color: hexCodeValidator,
  weightPx: z.number(),
});
const borderStrokeStyleDefinitionValidator: z.ZodType<BorderStrokeStyleDefinition> =
  borderStrokeStyleDefinitionValidator_;
export interface BorderStrokeStyleDefinition extends z.infer<
  typeof borderStrokeStyleDefinitionValidator_
> {}

const borderStyleDefinitionValidator_ = z.object({
  cornerRadiusPx: z.number(),
  stroke: borderStrokeStyleDefinitionValidator,
});
const borderStyleDefinitionValidator: z.ZodType<BorderStyleDefinition> =
  borderStyleDefinitionValidator_;
export interface BorderStyleDefinition extends z.infer<
  typeof borderStyleDefinitionValidator_
> {}

export const fontHierarchyKeys = ["h1", "h2", "h3", "body", "subtext"] as const;
export type FontHierarchyKey = (typeof fontHierarchyKeys)[number];
export const FontHierarchyKey = fontHierarchyKeys;

export enum SocialPlatform {
  APPLE = "apple",
  DISCORD = "discord",
  FACEBOOK = "facebook",
  GITHUB = "github",
  GOOGLE = "google",
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
  PINTEREST = "pinterest",
  REDDIT = "reddit",
  SNAPCHAT = "snapchat",
  TIKTOK = "tiktok",
  TWITTER = "twitter",
  YOUTUBE = "youtube",
}

const elementStyleDefinitionValidator_ = z.object({
  backgroundColor: hexCodeValidator.nullish(),
  textColor: hexCodeValidator,
  border: borderStyleDefinitionValidator,
  paddingPx: z.number(),
  fontReference: createEnum(fontHierarchyKeys, {
    enumName: "FontHierarchyKey",
  }),
});
const elementStyleDefinitionValidator: z.ZodType<ElementStyleDefinition> =
  elementStyleDefinitionValidator_;
export interface ElementStyleDefinition extends z.infer<
  typeof elementStyleDefinitionValidator_
> {}

const buttonStyleDefinitionValidator = elementStyleDefinitionValidator;
export interface ButtonStyleDefinition extends ElementStyleDefinition {}

const inputStyleDefinitionValidator_ = elementStyleDefinitionValidator_.extend({
  errorColor: hexCodeValidator,
});
const inputStyleDefinitionValidator: z.ZodType<InputStyleDefinition> =
  inputStyleDefinitionValidator_;
export interface InputStyleDefinition extends z.infer<
  typeof inputStyleDefinitionValidator_
> {}

const buttonOptionsValidator_ = z.object({
  primary: buttonStyleDefinitionValidator,
  secondary: buttonStyleDefinitionValidator,
  tertiary: buttonStyleDefinitionValidator,
});
const buttonOptionsValidator: z.ZodType<ButtonOptions> =
  buttonOptionsValidator_;
export interface ButtonOptions extends z.infer<
  typeof buttonOptionsValidator_
> {}

export type Override<T extends ElementStyleDefinition> = Omit<
  T,
  "fontReference"
> & { font: FontStyleDefinition & { fontFamily: string } };

export type ButtonOptionsOverride = {
  [K in keyof ButtonOptions]: Override<ButtonStyleDefinition>;
};

export const contactCardValidator = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.union([z.email(), z.literal("")]).nullish(),
  phone: z.string().nullish(),
  websiteUrl: z.union([z.url(), z.literal("")]).nullish(),
  logoUrl: z.string().nullish(),
  contactCardSrc: z.string().nullish(),
});
export interface ContactCard extends z.infer<typeof contactCardValidator> {}

export const customFontStyle = z.object({
  _id: z.string(),
  fontName: z.string(),
  fontStyle: z.string(), // e.g. "italic", "bold", "bold italic", "regular", .etc
  weight: z.string(), // e.g. "400", "600", "700"
  italic: z.boolean(),
  fontFileUrl: z.string(),
});

export type CustomFontStyle = z.infer<typeof customFontStyle>;

export const customFontFamily = z.object({
  _id: z.string(),
  fontFamily: z.string(),
  fallbackFont: z.string(),
  styles: z.array(customFontStyle),
});

export type CustomFontFamily = z.infer<typeof customFontFamily>;

export const brandKitValidator_ = z.object({
  colors: z.object({ background: z.string(), accent: z.string() }),
  customFontFamilies: z.array(customFontFamily).nullish(),
  font: z.object({
    /** @deprecated use `headerFontFamily` and `bodyFontFamily` instead */
    fontFamily: z.string(),
    headerFontFamily: z.string(),
    bodyFontFamily: z.string(),
    hierarchy: z.object({
      h1: fontStyleDefinitionValidator,
      h2: fontStyleDefinitionValidator,
      h3: fontStyleDefinitionValidator,
      body: fontStyleDefinitionValidator,
      subtext: fontStyleDefinitionValidator,
    }),
  }),
  socialLinks: z
    .partialRecord(
      createEnum(SocialPlatform, { enumName: "SocialPlatform" }),
      z.string().nullable(),
    )
    .nullish(),
  inputs: inputStyleDefinitionValidator,
  buttons: buttonOptionsValidator,
  /**
   * Instead of optional fields, empty strings are used to indicate no image.
   * (Makes it easier to work with the Inspector component)
   */
  images: z.object({
    logoUrl: z.string(),
    faviconUrl: z.string(),
    bannerUrl: z.string(),
    logoBannerUrl: z.string(),
  }),
  customerFacingDomain: z.string().nullish(),
  contactCard: contactCardValidator.nullish(),
});
export const brandKitValidator: z.ZodType<BrandKit> = brandKitValidator_;
export interface BrandKit extends z.infer<typeof brandKitValidator_> {}

export const defaultTertiaryButton: ButtonStyleDefinition = {
  backgroundColor: undefined,
  textColor: "#000000",
  border: { cornerRadiusPx: 8, stroke: { color: "#000000", weightPx: 0 } },
  paddingPx: 8,
  fontReference: "body",
};

export const defaultBrandKit: BrandKit = {
  colors: { background: "#ffffff", accent: "#000000" },
  font: {
    fontFamily: FontFamily.ARIAL,
    headerFontFamily: FontFamily.ARIAL,
    bodyFontFamily: FontFamily.ARIAL,
    hierarchy: {
      h1: { fontSizePx: 48, fontWeight: "bold" },
      h2: { fontSizePx: 36, fontWeight: "semibold" },
      h3: { fontSizePx: 30, fontWeight: "regular" },
      body: { fontSizePx: 16, fontWeight: "regular" },
      subtext: { fontSizePx: 14, fontWeight: "regular" },
    },
  },
  socialLinks: {},
  inputs: {
    backgroundColor: "#ffffff",
    errorColor: "#ff0000",
    textColor: "#000000",
    border: { cornerRadiusPx: 8, stroke: { color: "#000000", weightPx: 1 } },
    paddingPx: 8,
    fontReference: "body",
  },
  buttons: {
    primary: {
      backgroundColor: "#000000",
      textColor: "#ffffff",
      border: { cornerRadiusPx: 8, stroke: { color: "#000000", weightPx: 1 } },
      paddingPx: 8,
      fontReference: "body",
    },
    secondary: {
      backgroundColor: "#ffffff",
      textColor: "#000000",
      border: { cornerRadiusPx: 8, stroke: { color: "#000000", weightPx: 1 } },
      paddingPx: 8,
      fontReference: "body",
    },
    tertiary: defaultTertiaryButton,
  },
  images: { logoUrl: "", faviconUrl: "", bannerUrl: "", logoBannerUrl: "" },
};

/**
 * Validates a domain host format (no protocol, no path)
 * Valid: example.com, subdomain.example.com
 * Invalid: https://example.com, example.com/path, www.example.com/
 */
export function validateDomainHost(value?: string): string[] {
  const errors: string[] = [];
  if (!value || value.trim() === "") {
    return errors; // empty is allowed since it's optional
  }

  const trimmedValue = value.trim().toLowerCase();

  // Check for protocol
  if (trimmedValue.includes("://")) {
    errors.push("Domain should not include protocol (https://)");
  }

  // Check for path
  if (trimmedValue.includes("/")) {
    errors.push("Domain should not include path");
  }

  // Check for valid domain format (allows subdomains)
  const domainRegex =
    /^([a-z0-9]+(-[a-z0-9]+)*\.)*[a-z0-9]+(-[a-z0-9]+)*\.[a-z]{2,}$/;
  if (!domainRegex.test(trimmedValue)) {
    errors.push("Please enter a valid domain (e.g. example.com)");
  }

  return errors;
}
